import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StepIndicator } from '../components/StepIndicator';
import { OnboardingData, initialOnboardingData } from '../types/onboarding';
import { UserDetails } from '../components/steps/UserDetails';
import { StatesLicensed } from '../components/steps/StatesLicensed';
import { ReviewCreate } from '../components/steps/ReviewCreate';
import { OffboardingPanel } from '../components/OffboardingPanel';
const STEPS = [{
  id: 1,
  title: 'User Details',
  description: 'Enter the core information for the new Salesforce user and verify license availability.'
}, {
  id: 2,
  title: 'States Licensed',
  description: 'Select the states where the user is licensed to operate.'
}, {
  id: 3,
  title: 'Review & Create',
  description: 'Review all configurations before creating the user and associated records.'
}];
const OFFBOARDING_STEPS = [{
  id: 1,
  title: 'Select User',
  description: 'Identify the user being terminated.'
}, {
  id: 2,
  title: 'Review Config',
  description: 'Load and review offboarding metadata configuration.'
}, {
  id: 3,
  title: 'Reassignment',
  description: 'Reassign subordinates if applicable.'
}, {
  id: 4,
  title: 'Deactivation',
  description: 'Configure and review deactivation actions.'
}, {
  id: 5,
  title: 'Execute',
  description: 'Execute offboarding and verify completion.'
}];
interface OnboardingWizardProps {
  mode: 'onboarding' | 'offboarding';
  setMode: (mode: 'onboarding' | 'offboarding') => void;
}
export function OnboardingWizard({
  mode,
  setMode
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [offboardingStep, setOffboardingStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingData>(initialOnboardingData);
  const [canAdvance, setCanAdvance] = useState(false);
  const updateFormData = (updates: Partial<OnboardingData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canAdvance) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleStepClick = (index: number) => {
    if (index < currentStep) {
      setCurrentStep(index);
    }
  };
  const isLastStep = currentStep === STEPS.length - 1;
  const renderStepContent = () => {
    const props = {
      data: formData,
      updateData: updateFormData,
      setCanAdvance
    };
    switch (currentStep) {
      case 0:
        return <UserDetails {...props} data-id="element-475" />;
      case 1:
        return <StatesLicensed {...props} data-id="element-476" />;
      case 2:
        return <ReviewCreate {...props} onBack={handleBack} data-id="element-477" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen w-full bg-[#F0F2F5] font-sans text-gray-900 pb-12" data-id="element-478">
      {/* Top Header Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6" data-id="element-479">
        <div className="flex items-center justify-between" data-id="element-480">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex-shrink-0" data-id="element-481">
            <img src="https://cdn.magicpatterns.com/uploads/kmdDfAkePYiLdiWNbyTETK/2026_Large_Transparent_New_Logo_-_Use_this_Always.png" alt="Rapid Home Lending" className="h-28" data-id="element-482" />
          </motion.div>

          {/* License Counters */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="flex items-center gap-3" data-id="element-483">
            {/* Salesforce */}
            <div className="bg-white rounded-full px-3 py-1.5 shadow-sm border border-gray-100 flex items-center gap-2" data-id="element-484">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#2563EB] flex-shrink-0" fill="currentColor" data-id="element-485">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" data-id="element-486" />
              </svg>
              <div className="flex flex-col items-end leading-none" data-id="element-487">
                <span className="text-[9px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-488">
                  Salesforce
                </span>
                <span className="text-xs font-bold text-gray-900" data-id="element-489">
                  14<span className="text-gray-400 font-medium" data-id="element-490">/58</span>
                </span>
              </div>
            </div>

            {/* Pulse */}
            <div className="bg-white rounded-full px-3 py-1.5 shadow-sm border border-gray-100 flex items-center gap-2" data-id="element-491">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#7C3AED] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-492">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" data-id="element-493" />
              </svg>
              <div className="flex flex-col items-end leading-none" data-id="element-494">
                <span className="text-[9px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-495">
                  Pulse
                </span>
                <span className="text-xs font-bold text-gray-900" data-id="element-496">
                  6<span className="text-gray-400 font-medium" data-id="element-497">/52</span>
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-498">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row min-h-[600px] overflow-hidden" data-id="element-499">
          {/* Left Column: Navigation */}
          <div className="w-full md:w-[240px] border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0 bg-white" data-id="element-500">
            <StepIndicator steps={mode === 'offboarding' ? OFFBOARDING_STEPS : STEPS} currentStep={mode === 'offboarding' ? offboardingStep : currentStep} onStepClick={handleStepClick} mode={mode} setMode={setMode} data-id="element-501" />
          </div>

          {/* Right Column: Form Area */}
          <div className="flex-1 flex flex-col bg-white relative" data-id="element-502">
            {mode === 'offboarding' ? <OffboardingPanel onStepChange={setOffboardingStep} data-id="element-503" /> : <div className="flex-1 p-6 md:p-10 overflow-y-auto" data-id="element-504">
                {/* Step Header */}
                <div className="mb-8" data-id="element-505">
                  <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-506">
                    {STEPS[currentStep].title}
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-507">
                    {STEPS[currentStep].description}
                  </p>
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait" data-id="element-508">
                  <motion.div key={currentStep} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} data-id="element-509">
                    {renderStepContent()}
                  </motion.div>
                </AnimatePresence>

                {/* Inline Navigation (Hidden on last step as it handles its own submit) */}
                {!isLastStep && <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-510">
                    {currentStep > 0 ? <button onClick={handleBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-511">
                          Previous
                        </button> : <div data-id="element-512"></div> // Spacer
              }

                    <button onClick={handleNext} disabled={!canAdvance} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm ${!canAdvance ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]'}`} data-id="element-513">
                      Next
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
}